import { useState } from "react";
import { ArrowRight, Loader2, CheckCircle2, MapPin, Mail, Phone, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Reveal, PageHero } from "@/components/shared";
import { API, formatApiError } from "@/lib/api";
import { CONTACT, IMAGES } from "@/lib/content";

const EMPTY = { name: "", email: "", phone: "", business: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post("/submissions", { type: "contact", ...form });
      setDone(true);
      if (window.gtag) window.gtag("event", "contact_submitted", { location: "contact_page" });
      toast.success("Message received. We reply within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Let's discuss your digital growth."
        lead="Call, WhatsApp, email or send the form — a real person in Antigua answers every one."
        image={IMAGES.villaExt}
      />

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="gold-ring-card p-8 lg:p-10" data-testid="contact-form-card">
                {done ? (
                  <div className="flex flex-col items-center gap-5 py-14 text-center" data-testid="contact-success">
                    <CheckCircle2 className="h-16 w-16 text-gold" />
                    <h2 className="font-serif text-3xl text-ivory">Message received.</h2>
                    <p className="max-w-md text-sm font-light text-slate-400">Thank you — we reply within one business day, usually much sooner.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <input required value={form.name} onChange={set("name")} placeholder="Full name" className="input-lux" data-testid="contact-name-input" />
                      <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className="input-lux" data-testid="contact-email-input" />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <input value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className="input-lux" data-testid="contact-phone-input" />
                      <input value={form.business} onChange={set("business")} placeholder="Business name" className="input-lux" data-testid="contact-business-input" />
                    </div>
                    <textarea required value={form.message} onChange={set("message")} rows={5} placeholder="Tell us what you're trying to achieve…" className="input-lux resize-none" data-testid="contact-message-input" />
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                      className="group flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send message <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>

          <div className="space-y-6 lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="gold-ring-card p-8" data-testid="contact-details-card">
                <p className="eyebrow">Office</p>
                <ul className="mt-6 space-y-5 text-sm text-slate-300">
                  <li className="flex items-start gap-4"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold" /><span>{CONTACT.office}<br /><span className="text-xs text-slate-500">17.0608° N, 61.7964° W</span></span></li>
                  <li className="flex items-center gap-4"><Mail className="h-5 w-5 shrink-0 text-gold" />{CONTACT.email}</li>
                  <li className="flex items-start gap-4"><Phone className="mt-0.5 h-5 w-5 shrink-0 text-gold" /><span>{CONTACT.phone} <span className="text-slate-500">(Antigua)</span><br />{CONTACT.phone_uk} <span className="text-slate-500">(UK)</span></span></li>
                  <li className="flex items-center gap-4"><Clock className="h-5 w-5 shrink-0 text-gold" />{CONTACT.hours}</li>
                </ul>
                <div className="mt-8 space-y-3">
                  <a
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => window.gtag && window.gtag("event", "whatsapp_click", { location: "contact_page", line: "antigua" })}
                    data-testid="contact-whatsapp-btn"
                    className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02]"
                  >
                    <MessageCircle className="h-4 w-4" /> Chat on WhatsApp — Antigua
                  </a>
                  <a
                    href={CONTACT.whatsapp_uk}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => window.gtag && window.gtag("event", "whatsapp_click", { location: "contact_page", line: "uk" })}
                    data-testid="contact-whatsapp-uk-btn"
                    className="flex items-center justify-center gap-2 rounded-full border border-[#25D366]/50 px-6 py-3.5 text-sm font-semibold text-[#25D366] transition-colors duration-300 hover:bg-[#25D366]/10"
                  >
                    <MessageCircle className="h-4 w-4" /> UK clients — WhatsApp +44
                  </a>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="relative overflow-hidden rounded-2xl border border-gold/20" data-testid="contact-map-card">
                <img src={IMAGES.harbor} alt="An Antigua harbour at golden hour" className="h-56 w-full object-cover duotone opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 to-transparent" />
                <div className="absolute bottom-5 left-5">
                  <p className="font-serif text-lg text-ivory">Across Antigua &amp; Barbuda</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-gold/80">On-island service, wherever you are</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
